import { useState } from "react";
import DatePickers from "../../../library/custom/datePicker/DatePicker";
import "../reportDashboard.scss";

const PRESETS = [
  { key: "all", label: "All Time" },
  { key: "7", label: "Last 7 Days" },
  { key: "14", label: "Last 14 Days" },
  { key: "30", label: "Last 30 Days" },
  { key: "90", label: "Last 90 Days" },
  { key: "custom", label: "Custom" },
];

// Format a Date as yyyy-MM-dd using LOCAL calendar parts (not toISOString(),
// which converts to UTC and can roll the date forward a day in behind-UTC
// timezones right at the day boundary).
export function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// {from, to} ISO-date pair for a "last N days" preset, anchored on today
// (local) so the range always includes today as the end date.
export function presetToRange(presetKey) {
  if (presetKey === "all" || presetKey === "custom")
    return { from: null, to: null };
  const days = Number(presetKey);
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

// Today (local), capped as the latest selectable date on both custom inputs — the
// dashboard reports on what has happened, so a future From/To produced an empty or
// nonsensical range instead of being rejected up front.
const todayIso = () => toIsoDate(new Date());

export function fromIsoDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const DateRangeFilter = ({ value, onChange }) => {
  // Draft state for the two custom inputs. We only push the applied range up to
  // the parent (which is what triggers a data refetch) once BOTH ends are set
  // and valid — so typing one date, or entering custom mode, never fires a
  // request or wipes the currently-shown data.
  const [customFrom, setCustomFrom] = useState(value.from || "");
  const [customTo, setCustomTo] = useState(value.to || "");
  const maxDate = todayIso();

  const selectPreset = (presetKey) => {
    if (presetKey === "custom") {
      // Enter custom mode without refetching: keep the currently applied
      // from/to (so the deps the parent watches don't change) and seed the
      // draft inputs from them.
      setCustomFrom(value.from || "");
      setCustomTo(value.to || "");
      onChange({ ...value, preset: "custom" });
      return;
    }
    const range = presetToRange(presetKey);
    onChange({ preset: presetKey, from: range.from, to: range.to });
  };

  const commitCustom = (nextFrom, nextTo) => {
    // Apply (=> refetch) only when both ends are present, ordered, and not in the
    // future. The maxDate/minDate props below grey these out in the calendar, but a
    // value typed into the text field can still reach here, so this is the actual
    // enforcement rather than a second copy of it.
    if (nextFrom && nextTo && nextFrom <= nextTo && nextTo <= maxDate) {
      onChange({ preset: "custom", from: nextFrom, to: nextTo });
    }
  };

  const handleFromChange = (date) => {
    const next = date ? toIsoDate(date) : "";
    setCustomFrom(next);
    commitCustom(next, customTo);
  };

  const handleToChange = (date) => {
    const next = date ? toIsoDate(date) : "";
    setCustomTo(next);
    commitCustom(customFrom, next);
  };

  return (
    <div className="rdDateFilter">
      {PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          className={
            value.preset === p.key
              ? "rdDateFilter__btn active"
              : "rdDateFilter__btn"
          }
          onClick={() => selectPreset(p.key)}
        >
          {p.label}
        </button>
      ))}
      {value.preset === "custom" && (
        <div className="rdDateFilter__custom">
          {/* The app's shared MUI picker instead of <input type="date">: the native
              dropdown calendar is browser chrome and stayed light in dark mode. This
              one renders through the app's MUI theme (context.js sets palette.mode),
              so it matches every other calendar in the app. maxDate/minDate carry over
              the same bounds the native max/min attributes enforced; forceDesktop keeps
              the immediate-commit popper on touch, since the mobile OK/Cancel dialog
              drops the selection against our string state. */}
          <DatePickers
            name="rdRangeFrom"
            testId="rdRangeFrom"
            value={fromIsoDate(customFrom)}
            maxDate={fromIsoDate(customTo) || fromIsoDate(maxDate)}
            onChangeHandler={handleFromChange}
            forceDesktop
          />
          <span className="rdDateFilter__sep">to</span>
          <DatePickers
            name="rdRangeTo"
            testId="rdRangeTo"
            value={fromIsoDate(customTo)}
            minDate={fromIsoDate(customFrom) || undefined}
            maxDate={fromIsoDate(maxDate)}
            onChangeHandler={handleToChange}
            forceDesktop
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
